/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Empty, Modal, Typography } from 'antd';
import { FullscreenOutlined, ThunderboltOutlined } from '@ant-design/icons';
import AnalyticsTable from '../Tables/AnalyticsTable';
import EchartsWrapper from './EchartsWrapper';
import ChartLoader from './ChartLoader';

// Misc imports
import { formatChartData } from '../../utils/function/formatChartData';
import is from '../../utils/is';

const LiveChatResponseTimeSection = ({ data = {}, sectionTitle = '', isLoading = true, renderOnlyChart = false, renderOnlyTable = false }) => {
  const [chartToFullscreen, setChartToFullscreen] = useState();
  const { t } = useTranslation();

  const getOptions = (avgFirstResponsePerDay, avgResponsePerDay) => {
    const formattedFirstResponse = formatChartData(avgFirstResponsePerDay);
    const formattedResponse = formatChartData(avgResponsePerDay);
    const isLessThanAMinute = Math.max(...formattedFirstResponse.map(item => item.y), ...formattedResponse.map(item => item.y)) < 60;
    const xData = formattedFirstResponse.map(item => item.x);
    const yData = isLessThanAMinute
      ? formattedFirstResponse.map(item => item.y)
      : formattedFirstResponse.map(item => parseFloat(item.y / 60).toFixed(1));
    const y2Data = isLessThanAMinute
      ? formattedResponse.map(item => item.y)
      : formattedResponse.map(item => parseFloat(item.y / 60).toFixed(1));

    return {
      grid: {
        left: '1%',
        right: '4%',
        bottom: '2%',
        containLabel: true
      },
      legend: {
        show: true,
        top: '0%',
        right: '0%',
        orient: 'vertical',
        data: [t('Avg. First Response'), t('Avg. Response')]
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          show: true,
          type: 'none'
        },
        triggerOn: 'mousemove|click',
        enterable: true,
        renderMode: 'html',
        confine: true,
        formatter: (params) => {
          const firstResponse = params[0];
          const response = params[1];
          const firstResponseTime = isLessThanAMinute ? `${firstResponse.value} ${t('sec')}` : `${firstResponse.value} ${t('min')}`;
          const responseTime = isLessThanAMinute ? `${response?.value} ${t('sec')}` : `${response?.value} ${t('min')}`;

          return `${firstResponse.name}<br />${firstResponse.marker} ${t('Avg. First Response')}: ${firstResponseTime}<br />${response.marker} ${t('Avg. Response')}: ${responseTime}`;
        }
      },
      xAxis: [{
        type: 'category',
        position: 'bottom',
        axisTick: {
          show: true,
          alignWithLabel: true
        },
        axisLine: {
          show: false
        },
        axisLabel: {
          show: true,
          // How many values will be skipped between labels in the X Axis
          // e.x For 10 dates, this will write the first date, skip the next 8, and write the last
          interval: function(index, value) {
            if (xData.at(0) === value || xData.at(Math.floor(xData?.length/2)) === value || xData.at(-1) === value) {
              return true;
            }
          }
        },
        data: xData
      }],
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: isLessThanAMinute ? `${t('{value} sec')}` : `${t('{value} min')}`
          }
        }
      ],
      series: [
        {
          smooth: true,
          name: t('Avg. First Response'),
          type: 'line',
          showSymbol: true,
          data: yData
        },
        {
          smooth: true,
          name: t('Avg. Response'),
          type: 'line',
          showSymbol: true,
          data: y2Data
        }
      ]
    };
  };

  const tableConfig = {
    descriptionTableData: data,
    isDescriptionTable: true,
    isTableLoading: isLoading,
    emptyLines: 0,
    descriptionTableFields: [
      {
        label: t('Avg. First Response'),
        dataField: 'avgFirstResponse',
        isPercentage: false,
        isToBeFixed: true,
        isToBeConvertedToSecOrMinutes: true
      },
      {
        label: t('Longest First Response'),
        dataField: 'longestFirstResponse',
        isPercentage: false,
        isToBeFixed: true,
        isToBeConvertedToSecOrMinutes: true
      },
      {
        label: t('Shortest First Response'),
        dataField: 'shortestFirstResponse',
        isPercentage: false,
        isToBeFixed: true,
        isToBeConvertedToSecOrMinutes: true
      },
      {
        label: t('Avg. Response'),
        dataField: 'avgResponse',
        isPercentage: false,
        isToBeFixed: true,
        isToBeConvertedToSecOrMinutes: true
      },
      {
        label: t('Longest Response'),
        dataField: 'longestResponse',
        isPercentage: false,
        isToBeFixed: true,
        isToBeConvertedToSecOrMinutes: true
      },
      {
        label: t('Shortest Response'),
        dataField: 'shortestResponse',
        isPercentage: false,
        isToBeFixed: true,
        isToBeConvertedToSecOrMinutes: true
      }
    ]
  };

  const chart = (
    <Fragment>
      {
        data?.avgFirstResponsePerDay?.length > 0 || data?.avgResponsePerDay?.length > 0
          ? <EchartsWrapper
            chartOptions={getOptions(data.avgFirstResponsePerDay, data.avgResponsePerDay)}
            style={{ height: 367 }}
            loading={isLoading}
          />
          : <div className="d-flex content-fill flex-column h-100">
            <div className="mt-5 d-flex align-items-center mb-auto text-center flex-auto align-self-center">
              <Empty
                description={(
                  <Typography.Paragraph>
                    {t('No data available for this chart')}
                  </Typography.Paragraph>
                )}

              />
            </div>
          </div>
      }
    </Fragment>
  );

  return (
    <Fragment>
      {
        renderOnlyChart
          ? chart
          : <Fragment>
            {
              renderOnlyTable
                ? <AnalyticsTable
                  descriptionTableData={tableConfig.descriptionTableData}
                  isDescriptionTable={tableConfig.isDescriptionTable}
                  isTableLoading={tableConfig.isTableLoading}
                  emptyLines={tableConfig.emptyLines}
                  descriptionTableFields={tableConfig.descriptionTableFields}
                />
                : <Fragment>
                  <div className="mb-6 position-relative">
                    <Typography.Title level={4} className="mb-6">
                      <Typography.Text className="mr-2">{sectionTitle}</Typography.Text>
                      <ThunderboltOutlined className="ml-2" />
                    </Typography.Title>

                    {isLoading ? <ChartLoader /> : chart}

                    <Button
                      type="text"
                      size="large"
                      disabled={isLoading}
                      icon={<FullscreenOutlined />}
                      className="position-absolute top-0 right-0"
                      onClick={() => setChartToFullscreen(chart)}
                    />
                  </div>

                  <AnalyticsTable
                    descriptionTableData={tableConfig.descriptionTableData}
                    isDescriptionTable={tableConfig.isDescriptionTable}
                    isTableLoading={tableConfig.isTableLoading}
                    emptyLines={tableConfig.emptyLines}
                    descriptionTableFields={tableConfig.descriptionTableFields}
                  />

                  <Modal
                    open={is.truthy(chartToFullscreen)}
                    onCancel={() => setChartToFullscreen()}
                    destroyOnClose
                    footer={null}
                    centered
                    style={{ maxWidth: 1000 }}
                    width={'95%'}
                  >
                    <div className="p-5">
                      {chartToFullscreen}
                    </div>
                  </Modal>
                </Fragment>
            }
          </Fragment>
      }
    </Fragment>
  );
};

LiveChatResponseTimeSection.propTypes = {
  data: PropTypes.object,
  sectionTitle: PropTypes.string,
  isLoading: PropTypes.bool,
  renderOnlyChart: PropTypes.bool,
  renderOnlyTable: PropTypes.bool
};

export default LiveChatResponseTimeSection;
